import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BankType } from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import {
  AssignBeneficiaryDto,
  Bank,
  VerifyAccount,
  VerifyAccountResponse
} from 'service/dto/bank.dto';
import {
  AssignTrusteeDto,
  BeneficiariesAllocationDto
} from 'service/dto/general.dto';

// type QueryParams = {
//   bankCode: string;
//   accountNo: string;
//   enabled: boolean;
// };

export const useCreateBank = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (bank: BankType): Promise<void> => {
      return services.bankService.createBank(bank);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-bank']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

// export const useVerifyBankAccount = ({
//   bankCode,
//   accountNo,
//   enabled
// }: QueryParams) => {
//   const verifyAccount = useQuery<any, AxiosError<Error>>(
//     ['verify_account'],
//     () => services.bankService.verifyBankAccount({ bankCode, accountNo }),
//     {
//       keepPreviousData: false,
//       refetchOnWindowFocus: false,
//       enabled
//     }
//   );

//   return verifyAccount;
// };
export const useVerifyBankAccount = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (verifyAccount: VerifyAccount): Promise<VerifyAccountResponse> => {
      return services.bankService.verifyBankAccount(verifyAccount);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['verify-bank-account']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetBankList = () =>
  useQuery(['banks'], () => services.bankService.getBanks(), {
    refetchOnWindowFocus: false
  });

export const useGetNigeriaBanks = () =>
  useQuery(['bank-list'], () => services.bankService.getNigeriaBanks(), {
    refetchOnWindowFocus: false
  });

export const useDeleteBank = () =>
  useMutation((bankId: number) => services.bankService.deleteBank(bankId), {
    onError: (error: Error) => {
      toast.error(<Alert message={error.message} type="error" />);
    }
  });

export const useVerifyBVN = () =>
  useMutation((bvn: string) => services.bankService.verifyBVN(bvn), {
    onError: (error: Error) => {
      toast.error(<Alert message={error.message} type="error" />);
    }
  });

export const useAssignBankBeneficiary = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (assignBeneficiaryDto: AssignBeneficiaryDto): Promise<void> => {
      return services.bankService.assignBeneficiary(assignBeneficiaryDto);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-bank-beneficiary']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetBankDetails = ({ bankId }: { bankId: number }) => {
  return useQuery<Bank, AxiosError<Error>>(
    ['get_bank', bankId],
    () => services.bankService.getBank(bankId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!bankId
    }
  );
};

export const useUnAssignBankBeneficiary = () =>
  useMutation(
    ({ allocationId }: { allocationId?: number }) =>
      services.bankService.unAssignBankBeneficiary(allocationId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useBankAllocationBulkEdit = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (beneficiariesAllocationDto: BeneficiariesAllocationDto): Promise<void> => {
      return services.bankService.editBulkBankBeneficiary(
        beneficiariesAllocationDto
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bank-allocation-edit']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useRemoveBankTrustee = () =>
  useMutation(
    ({ assetId, trusteeId }: { assetId: number; trusteeId: number }) =>
      services.bankService.removeBankTrustee(assetId, trusteeId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignBankTrustee = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ assetId, trusteeId, note }: AssignTrusteeDto): Promise<void> => {
      return services.bankService.assignBankTrustee(assetId, trusteeId, note);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-bank-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};
